(() => {
  if (window.__byzaynSocialEngagementFix) return;
  window.__byzaynSocialEngagementFix = true;

  const LIKE_KEY = 'byzayn-social-guest-likes-v2';
  const TOKEN_KEY = 'byzayn-social-guest-token-v1';
  const COMMENT_KEY = 'byzayn-social-pending-comment-v1';
  const activeCards = new WeakMap();
  const syncTimers = new WeakMap();
  let tokenPromise = null;

  const readLikes = () => {
    try {
      const value = JSON.parse(localStorage.getItem(LIKE_KEY) || '{}');
      return value && typeof value === 'object' ? value : {};
    } catch (_) {
      return {};
    }
  };

  const writeLikes = (likes) => {
    try {
      localStorage.setItem(LIKE_KEY, JSON.stringify(likes));
    } catch (_) {}
  };

  const readToken = () => {
    try {
      return localStorage.getItem(TOKEN_KEY) || '';
    } catch (_) {
      return '';
    }
  };

  const writeToken = (token) => {
    try {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
    } catch (_) {}
    window.__byzaynGuestLikeToken = token || '';
  };

  const countNode = (button) => button?.querySelector('[data-social-like-count]');

  const setCount = (button, value) => {
    const node = countNode(button);
    if (!node) return;
    const count = Math.max(0, Number(value) || 0);
    node.textContent = count > 999 ? '999+' : String(count);
    node.hidden = count === 0;
  };

  const setLikeState = (feed, button, liked, count) => {
    if (!(button instanceof HTMLButtonElement)) return;
    button.setAttribute('aria-pressed', liked ? 'true' : 'false');
    button.setAttribute('aria-label', liked ? (feed.dataset.unlikeLabel || 'Unlike') : (feed.dataset.likeLabel || 'Like'));
    if (typeof count === 'number' && Number.isFinite(count)) setCount(button, count);
  };

  const rememberLike = (productId, liked) => {
    if (!productId) return;
    const likes = readLikes();
    if (liked) likes[productId] = { liked: true, synced: true };
    else delete likes[productId];
    writeLikes(likes);
  };

  const applyStoredGuestLikes = (feed) => {
    if (!(feed instanceof HTMLElement) || feed.dataset.authenticated === 'true') return;
    const likes = readLikes();
    feed.querySelectorAll('[data-social-card]').forEach((card) => {
      const id = card.dataset.productId;
      const state = id ? likes[id] : null;
      if (!state?.liked || state.synced !== true) return;
      const button = card.querySelector('[data-social-like]');
      if (!(button instanceof HTMLButtonElement)) return;
      if (button.getAttribute('aria-pressed') === 'true') return;
      setLikeState(feed, button, true);
    });
  };

  const endpointUrl = (feed) => new URL(feed.dataset.endpoint || '/apps/byzayn-community', location.origin);

  const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {})
      }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) {
      const error = new Error(data.error || 'Engagement unavailable.');
      error.status = response.status;
      throw error;
    }
    return data;
  };

  const getGuestToken = async (feed, force = false) => {
    if (!(feed instanceof HTMLElement) || feed.dataset.authenticated === 'true') return '';
    if (!force) {
      const stored = readToken();
      if (stored) {
        window.__byzaynGuestLikeToken = stored;
        return stored;
      }
      if (tokenPromise) return tokenPromise;
    }

    tokenPromise = (async () => {
      const url = endpointUrl(feed);
      url.searchParams.set('view', 'identity');
      const data = await fetchJson(url);
      const token = typeof data.guestToken === 'string' ? data.guestToken : '';
      if (!token) throw new Error('Guest identity unavailable.');
      writeToken(token);
      return token;
    })();

    try {
      return await tokenPromise;
    } finally {
      tokenPromise = null;
    }
  };

  const syncGuestStats = async (feed, retried = false) => {
    if (!(feed instanceof HTMLElement) || feed.dataset.authenticated === 'true') return;
    const cards = [...feed.querySelectorAll('[data-social-card]')];
    const ids = [...new Set(cards.map((card) => card.dataset.productId).filter(Boolean))];
    if (!ids.length) return;

    try {
      const token = await getGuestToken(feed, retried);
      for (let index = 0; index < ids.length; index += 50) {
        const batch = ids.slice(index, index + 50);
        const url = endpointUrl(feed);
        url.searchParams.set('view', 'feed');
        url.searchParams.set('product_ids', batch.join(','));
        url.searchParams.set('guest_token', token);
        const data = await fetchJson(url);
        cards.forEach((card) => {
          const engagement = data.stats?.[card.dataset.productId];
          if (!engagement) return;
          const button = card.querySelector('[data-social-like]');
          setLikeState(feed, button, !!engagement.liked, Number(engagement.likeCount || 0));
          rememberLike(card.dataset.productId, !!engagement.liked);
        });
      }
    } catch (error) {
      if (error?.status === 401 && !retried) {
        writeToken('');
        await syncGuestStats(feed, true);
      }
    }
  };

  const scheduleGuestStats = (feed) => {
    if (!(feed instanceof HTMLElement) || feed.dataset.authenticated === 'true') return;
    const current = syncTimers.get(feed);
    if (current) clearTimeout(current);
    const timer = setTimeout(() => {
      syncTimers.delete(feed);
      void syncGuestStats(feed);
    }, 120);
    syncTimers.set(feed, timer);
  };

  const installGuestCommentForm = (feed) => {
    if (!(feed instanceof HTMLElement) || feed.dataset.authenticated === 'true') return;
    const signin = feed.querySelector('.bz-social-dialog__signin');
    if (!signin) return;

    const form = document.createElement('form');
    form.className = 'bz-social-comment-form';
    form.dataset.bzGuestCommentForm = '';
    form.innerHTML = [
      '<label>Join the conversation</label>',
      '<textarea name="body" minlength="2" maxlength="500" rows="3" required></textarea>',
      '<div><small>Sign in is required only when you post.</small><button type="submit">POST COMMENT</button></div>'
    ].join('');
    signin.replaceWith(form);
  };

  const prepareFeed = (feed) => {
    if (!(feed instanceof HTMLElement)) return;
    installGuestCommentForm(feed);
    applyStoredGuestLikes(feed);
    scheduleGuestStats(feed);

    if (feed.dataset.bzEngagementObserved === 'true') return;
    feed.dataset.bzEngagementObserved = 'true';
    const observer = new MutationObserver((records) => {
      installGuestCommentForm(feed);
      applyStoredGuestLikes(feed);
      if (records.some((record) => record.type === 'childList')) scheduleGuestStats(feed);
    });
    observer.observe(feed, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-pressed']
    });
  };

  const boot = () => {
    document.querySelectorAll('byzayn-social-feed').forEach(prepareFeed);
    const observer = new MutationObserver(() => document.querySelectorAll('byzayn-social-feed').forEach(prepareFeed));
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  const request = async (feed, body) => {
    const data = await fetchJson(feed.dataset.endpoint || '/apps/byzayn-community', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    if (!data.engagement) throw new Error('Engagement unavailable.');
    return data;
  };

  const handleGuestLike = async (feed, card, button, retried = false) => {
    if (button.disabled) return;
    const productId = card.dataset.productId;
    if (!productId) return;

    button.disabled = true;
    try {
      const guestToken = await getGuestToken(feed, retried);
      const data = await request(feed, { type: 'like', productId, guestToken });
      const engagement = data.engagement || {};
      const liked = !!engagement.liked;
      rememberLike(productId, liked);
      setLikeState(feed, button, liked, Number(engagement.likeCount || 0));
    } catch (error) {
      if (error?.status === 401 && !retried) {
        writeToken('');
        button.disabled = false;
        await handleGuestLike(feed, card, button, true);
        return;
      }
      const status = feed.querySelector('[data-social-status]');
      if (status) status.textContent = error instanceof Error ? error.message : (feed.dataset.error || 'Engagement unavailable.');
    } finally {
      button.disabled = false;
    }
  };

  const savePendingComment = (feed, card, body) => {
    try {
      sessionStorage.setItem(COMMENT_KEY, JSON.stringify({
        productId: card?.dataset.productId || '',
        productTitle: card?.dataset.productTitle || '',
        body,
        savedAt: Date.now()
      }));
    } catch (_) {}
  };

  const restorePendingComment = () => {
    let pending;
    try {
      pending = JSON.parse(sessionStorage.getItem(COMMENT_KEY) || 'null');
    } catch (_) {
      pending = null;
    }
    if (!pending?.productId || !pending?.body) return;
    if (Date.now() - Number(pending.savedAt || 0) > 30 * 60 * 1000) {
      try { sessionStorage.removeItem(COMMENT_KEY); } catch (_) {}
      return;
    }

    let attempts = 0;
    const restore = () => {
      attempts += 1;
      const feeds = [...document.querySelectorAll('byzayn-social-feed[data-authenticated="true"]')];
      for (const feed of feeds) {
        const card = [...feed.querySelectorAll('[data-social-card]')].find((node) => node.dataset.productId === pending.productId);
        if (!card) continue;
        const commentsButton = card.querySelector('[data-social-comments]');
        const textarea = feed.querySelector('[data-social-comment-form] textarea[name="body"]');
        if (!(commentsButton instanceof HTMLButtonElement) || !(textarea instanceof HTMLTextAreaElement)) continue;
        commentsButton.click();
        textarea.value = pending.body;
        textarea.focus();
        try { sessionStorage.removeItem(COMMENT_KEY); } catch (_) {}
        return;
      }
      if (attempts < 24) window.setTimeout(restore, 250);
    };
    restore();
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const comments = target.closest('[data-social-comments]');
    if (comments) {
      const feed = comments.closest('byzayn-social-feed');
      const card = comments.closest('[data-social-card]');
      if (feed && card) activeCards.set(feed, card);
    }

    const like = target.closest('[data-social-like]');
    if (!(like instanceof HTMLButtonElement)) return;
    const feed = like.closest('byzayn-social-feed');
    if (!(feed instanceof HTMLElement) || feed.dataset.authenticated === 'true') return;
    const card = like.closest('[data-social-card]');
    if (!(card instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    void handleGuestLike(feed, card, like);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-bz-guest-comment-form]')) return;
    const feed = form.closest('byzayn-social-feed');
    if (!(feed instanceof HTMLElement) || feed.dataset.authenticated === 'true') return;

    event.preventDefault();
    event.stopPropagation();
    const textarea = form.querySelector('textarea[name="body"]');
    const body = textarea?.value.trim() || '';
    if (body.length < 2) return;

    const card = activeCards.get(feed);
    savePendingComment(feed, card, body);
    const loginUrl = window.__byzaynStorefrontLoginUrl || '/customer_authentication/login';
    window.location.assign(loginUrl);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      boot();
      restorePendingComment();
    }, { once: true });
  } else {
    boot();
    restorePendingComment();
  }
})();
